import type { ComponentThemeStyles } from './types';

export const standardTheme: ComponentThemeStyles = {
  typography: {
    headingFont: 'Inter, system-ui, sans-serif',
    bodyFont: 'Inter, system-ui, sans-serif',
    baseSize: '16px',
  },
  navbar: {
    background: 'bg-white',
    text: 'text-gray-900',
    brandText: 'text-gray-900',
    linkText: 'text-gray-700',
    linkHover: 'hover:text-primary-600',
    border: 'border-b border-gray-200',
    shadow: 'shadow-sm',
  },
  navigationMenu: {
    background: 'bg-white',
    text: 'text-gray-900',
    brandText: 'text-gray-900',
    linkText: 'text-gray-700',
    linkHover: 'hover:text-primary-600',
    border: 'border-b border-gray-200',
    shadow: 'shadow-sm',
    dropdownBackground: 'bg-white',
    dropdownText: 'text-gray-700',
    dropdownHover: 'hover:bg-gray-100',
  },
  hero: {
    background: 'bg-gradient-to-br from-primary-600 to-primary-800',
    text: 'text-white',
    buttonStyle: 'btn-primary',
    overlay: 'bg-black/10',
    pattern: true,
  },
  splitHero: {
    background: '#ffffff',
    text: '#374151',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    taglineColor: '#3b82f6',
    buttonBg: '#3b82f6',
    buttonText: '#ffffff',
    buttonHover: '#2563eb',
  },
  htmlSplit: {
    background: '#ffffff',
    text: '#374151',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    taglineColor: '#3b82f6',
    buttonBg: '#3b82f6',
    buttonText: '#ffffff',
    buttonHover: '#2563eb',
  },
  simpleHeader: {
    background: '#ffffff',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
  },
  features: {
    background: 'bg-gray-50',
    cardBg: 'bg-white',
    iconColor: 'text-primary-600',
    titleColor: 'text-gray-900',
    textColor: 'text-gray-600',
    borderRadius: 'rounded-lg',
  },
  footer: {
    background: 'bg-gray-900',
    text: 'text-white',
    mutedText: 'text-gray-400',
    border: 'border-t border-gray-800',
  },
  cta: {
    background: 'bg-gradient-to-r from-primary-600 to-primary-800',
    text: 'text-white',
    buttonStyle: 'btn-primary',
  },
  form: {
    background: 'bg-white',
    inputBg: 'bg-white',
    inputBorder: 'border-gray-300',
    inputText: 'text-gray-900',
    labelColor: 'text-gray-700',
    buttonStyle: 'btn-primary',
  },
  pricing: {
    background: 'bg-white',
    cardBg: 'bg-white',
    highlightedCard: 'bg-gradient-to-br from-blue-50 to-blue-100',
    titleColor: 'text-gray-900',
    subtitleColor: 'text-gray-600',
    descriptionColor: 'text-gray-500',
    planTitle: 'text-gray-900',
    planDescription: 'text-gray-600',
    priceColor: 'text-gray-900',
    periodColor: 'text-gray-600',
    featureColor: 'text-gray-700',
    checkColor: '#3b82f6',
    highlightedTitle: 'text-blue-900',
    highlightText: '#1e3a8a',
    highlightedDescription: 'text-blue-700',
    highlightedPrice: 'text-blue-900',
    highlightedPeriod: 'text-blue-700',
    highlightedFeature: 'text-blue-800',
    highlightedCheckColor: '#1e40af',
    borderColor: 'border-gray-200',
    highlightBorder: 'border-blue-300',
    highlightRing: 'ring-blue-500',
    hoverGlow: 'hover:shadow-blue-500/25',
    badgeBg: '#1e40af',
    badgeText: 'white',
    buttonBg: '#3b82f6',
    highlightedButtonBg: '#1e40af',
  },
  mediaText: {
    background: '#ffffff',
    titleColor: '#111827',
    textColor: '#4b5563',
    accentColor: '#3b82f6',
  },
  faq: {
    background: '#f9fafb',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    questionColor: '#111827',
    answerColor: '#4b5563',
    itemBackground: '#ffffff',
    itemBorder: '#e5e7eb',
    iconColor: '#3b82f6',
  },
  testimonials: {
    background: '#ffffff',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    cardBackground: '#f9fafb',
    cardBorder: '#e5e7eb',
    quoteColor: '#374151',
    nameColor: '#111827',
    roleColor: '#6b7280',
    starColor: '#fbbf24',
  },
  countdownTimer: {
    background: '#f9fafb',
    text: '#111827',
    headline: '#111827',
    subheadline: '#6b7280',
    number: '#3b82f6',
    label: '#6b7280',
  },
  imageGallery: {
    background: '#ffffff',
    text: '#111827',
    title: '#111827',
    subtitle: '#6b7280',
    caption: '#4b5563',
    filterButton: '#e5e7eb',
    filterButtonActive: '#3b82f6',
  },
  imageInfo: {
    background: '#ffffff',
    text: '#111827',
    title: '#111827',
    subtitle: '#6b7280',
    imageBorder: '#e5e7eb',
    buttonBg: '#3b82f6',
    buttonText: '#ffffff',
  },
  imageCard: {
    background: '#ffffff',
    overlayColor: '#000000',
    title: '#ffffff',
    description: '#e5e7eb',
    buttonBg: '#3b82f6',
    buttonText: '#ffffff',
  },
  team: {
    background: '#f9fafb',
    cardBg: '#ffffff',
    nameColor: '#111827',
    roleColor: '#6b7280',
    borderColor: '#e5e7eb',
  },
  video: {
    background: '#ffffff',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    textColor: '#4b5563',
  },
  textBlocks: {
    background: '#ffffff',
    titleColor: '#111827',
    textColor: '#4b5563',
    linkColor: '#3b82f6',
  },
  titleSubtitle: {
    background: '#ffffff',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    buttonBg: '#3b82f6',
    buttonText: '#ffffff',
  },
  contactInfo: {
    background: '#f9fafb',
    titleColor: '#111827',
    textColor: '#4b5563',
    accentColor: '#3b82f6',
  },
  headContent: {},
  bodyEnd: {},
  htmlEmbed: {
    background: '#ffffff',
  },
  promoBanner: {
    background: '#3b82f6',
    text: '#ffffff',
    linkText: '#ffffff',
    dismissButton: '#ffffff',
  },
  logocloud: {
    background: '#f9fafb',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
  },
  stats: {
    background: '#ffffff',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    valueColor: '#3b82f6',
    labelColor: '#111827',
    descriptionColor: '#6b7280',
  },
  blogPreview: {
    background: '#f9fafb',
    titleColor: '#111827',
    subtitleColor: '#6b7280',
    cardBackground: '#ffffff',
    cardTitle: '#111827',
    cardExcerpt: '#4b5563',
    linkColor: '#3b82f6',
  },
  conversionPopup: {
    overlay: 'rgba(0,0,0,0.55)',
    panelBackground: '#ffffff',
    headlineColor: '#111827',
    bodyTextColor: '#374151',
    buttonBackground: '#3b82f6',
    buttonText: '#ffffff',
    closeButton: '#6b7280',
  },
  blog: {
    pageBackground: '#f9fafb',
    headerBackground: '#ffffff',
    titleColor: '#111827',
    descriptionColor: '#4b5563',
    textColor: '#4b5563',
    mutedTextColor: '#6b7280',
    linkColor: '#3b82f6',
    linkHoverColor: '#2563eb',
    cardBackground: '#ffffff',
    cardBorder: '#e5e7eb',
    cardShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    cardHoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    postTitleColor: '#111827',
    postTextColor: '#374151',
    postMetaColor: '#6b7280',
    tagBackground: '#f3f4f6',
    tagText: '#4b5563',
    tagActiveBackground: '#dbeafe',
    tagActiveText: '#1e40af',
    sidebarBackground: '#ffffff',
    sidebarTitleColor: '#111827',
    paginationBackground: '#ffffff',
    paginationText: '#374151',
    paginationBorder: '#d1d5db',
    paginationActiveBackground: '#3b82f6',
    paginationActiveText: '#ffffff',
    paginationDisabledOpacity: '0.5',
    iconColor: '#6b7280',
    loadingSpinnerColor: '#3b82f6',
    errorIconColor: '#9ca3af',
    errorButtonBackground: '#3b82f6',
    errorButtonText: '#ffffff',
  },
};
